import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

/**
 * 時間前の札が GC (取引締切) を迎えたか。 詳細モーダルの操作・約定結果の集計は
 * この 1 か所で判定し、 `unknown` の扱いは呼び出し側が明示する (#7430)。
 *
 * - `closed`: GC 到達済み。 境界 (`now === gcAt`) は閉鎖済み扱いで、 backend の
 *   `!now.Before(GcAt)` (編集・板取消・承認のゲート) と揃える
 * - `open`: GC 前
 * - `unknown`: `gcAt` が無く判定できない。 proto 上は必須なので通常は来ない。
 *   操作 (取下・承認系) は止め、 集計は数字が出る側に倒す
 */
export type IntradayGatePhase = 'open' | 'closed' | 'unknown'

export const intradayGatePhase = (
  gcAt: Timestamp | undefined,
  now: Date,
): IntradayGatePhase => {
  if (gcAt === undefined) return 'unknown'
  return now.getTime() >= timestampDate(gcAt).getTime() ? 'closed' : 'open'
}

/**
 * 札を編集できるか = 下書きで GC 前 (AC-BIN-007 / §市場フェーズ「GC 済みのコマの入札作成・
 * 編集は不可」)。 GC が判定できない時は fail-closed で編集不可。 詳細モーダルの編集可否と
 * 一覧の行の編集ボタン (#10110) がこの 1 か所で判定する (= ボタンを押すと表示専用の
 * モーダルが開く、 という食い違いを作らない)。
 */
export const isIntradayDraftEditable = (
  approvalState: BiddingApprovalState | undefined,
  gcAt: Timestamp | undefined,
  now: Date,
): boolean =>
  approvalState === BiddingApprovalState.DRAFT &&
  intradayGatePhase(gcAt, now) === 'open'

/**
 * 承認待ち・差戻しの札の行操作 (承認・差戻し AC-BIN-099、 申請取消 AC-BIN-048、 再編集
 * AC-BIN-049c) を GC で止める理由の文言。 操作できるなら undefined。 詳細モーダルと承認・差戻しの
 * hook (`useIntradayApprovalDecision`) が、 GC の段階をこの 1 か所で文言にする。
 *
 * `gcAt` が無い札は判定材料が無いので止める (fail-closed) が、 「GC を過ぎている」 とは
 * 断定できないので文言を分ける。 差戻しの札で押せるのは再編集だけなので、 `reopen` では
 * その操作に絞った文言にする (design/bidding/intraday.html の再編集ボタンの無効時の説明)。
 */
export const intradayRowOperationGateBlockedReason = (
  t: TFunction,
  gcAt: Timestamp | undefined,
  at: Date,
  operation: 'approval' | 'reopen' = 'approval',
): string | undefined => {
  switch (intradayGatePhase(gcAt, at)) {
    case 'unknown':
      return t(
        operation === 'reopen'
          ? 'page:biddingIntraday.approvalAction.reopenGcUnknown'
          : 'page:biddingIntraday.approvalAction.gcUnknown',
      )
    case 'closed':
      return t(
        operation === 'reopen'
          ? 'page:biddingIntraday.approvalAction.reopenGcPassed'
          : 'page:biddingIntraday.approvalAction.gcPassed',
      )
    case 'open':
      return undefined
  }
}
